import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import Header from '@/components/Header'
import Back from '@/components/Back'
import FixedBottom from '@/components/FixedBottom'
import CustomButton from '@/components/CustomButton'
import UploadFile from '@/components/UploadFile'
import { type KycFormData, DocumentType, KycMode } from '../types'

interface StepUploadHandheldProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  onNext: () => void
  onBack: () => void
  mode: KycMode
  canProceed?: boolean
  submitting?: boolean
}

export default function StepUploadHandheld({ formData, updateFormData, onNext, onBack, mode, canProceed = true, submitting }: StepUploadHandheldProps) {
  const { t } = useTranslation()
  const isIdCard = formData.identity_type === DocumentType.IdCard
  const isReadonly = mode === KycMode.View
  const [handheldFile, setHandheldFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  // 处理手持证件图片变化（包括删除）
  const handleHandheldFileChange = useCallback((file: File | null) => {
    setHandheldFile(file)
    // 如果删除了图片，同时清除formData中的URL
    if (!file) {
      if (isIdCard) {
        updateFormData({ idCardHandheldUrl: '' })
      } else {
        updateFormData({ passportHandheldUrl: '' })
      }
    }
  }, [isIdCard, updateFormData])

  const handleNext = useCallback(() => {
    if (canProceed && !uploading) {
      onNext()
    }
  }, [onNext, canProceed, uploading])

  // 处理上传成功
  const handleHandheldUploadSuccess = useCallback((url: string) => {
    if (isIdCard) {
      updateFormData({ idCardHandheldUrl: url })
    } else {
      updateFormData({ passportHandheldUrl: url })
    }
    setUploading(false)
  }, [isIdCard, updateFormData])

  const handleUploadStart = useCallback(() => {
    setUploading(true)
  }, [])

  const handleUploadError = useCallback((error: string) => {
    setUploading(false)
    Toast.fail(error || t('上传失败'))
  }, [t])

  const getTitle = () => {
    return t('上传手持证件照片')
  }

  const getSubtitle = () => {
    return t('请上传本人手持证件的正面照片')
  }

  // 是否禁用按钮
  const isButtonDisabled = !canProceed || uploading || submitting

  return (
    <div className="flex flex-col h-full">
      <Header
        leftChildren={<Back onClick={onBack} />}
      />

      <div className="flex-1 px-5 pt-4 pb-24 overflow-y-auto">
        {/* 标题 */}
        <div className="text-7 font-medium text-[#1D2129] mb-3">
          {getTitle()}
        </div>

        {/* 副标题 */}
        <p className="text-base text-[#9BA0AF] mb-6">
          {getSubtitle()}
        </p>

        {/* 手持证件上传区域 */}
        <UploadFile
          value={handheldFile}
          onChange={handleHandheldFileChange}
          onUploadSuccess={handleHandheldUploadSuccess}
          onUploadStart={handleUploadStart}
          onUploadError={handleUploadError}
          placeholder="/images/my/handheld-placeholder.png"
          uploadedUrl={isIdCard ? formData.idCardHandheldUrl : formData.passportHandheldUrl}
          autoUpload
          maxSize={2}
          className="aspect-[16/9.42]"
          uploadIcon="/images/my/icons/camera.svg"
        />

        {/* 提示 */}
        <div className="mt-6 space-y-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5">
              {/* 文档图标 */}
              <img src="/icon/info-success.svg" />
            </div>
            <span className="text-sm text-[#1D2129]">{t('图片大小需小于maxSizeM', { maxSize: 2 })}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5">
              {/* 人物图标 */}
              <img src="/icon/info-success.svg" />
            </div>
            <span className="text-sm text-[#1D2129]">{t('上传原始手持证件照片')}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5">
              {/* 人物图标 */}
              <img src="/icon/info-success.svg" />
            </div>
            <span className="text-sm text-[#1D2129]">{t('需本人亲自手持证件拍摄，面部正对镜头')}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5">
              {/* 人物图标 */}
              <img src="/icon/info-success.svg" />
            </div>
            <span className="text-sm text-[#1D2129]">{t('确保五官与证件信息清晰可辨')}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5">
              {/* 人物图标 */}
              <img src="/icon/info-success.svg" />
            </div>
            <span className="text-sm text-[#1D2129]">{t('请勿上传镜像、翻转、遮挡或模糊的照片')}</span>
          </div>
        </div>
      </div>

      {/* 底部按钮 */}
      {!isReadonly && (
        <FixedBottom className="bg-white">
          <div className='text-sm text-[#9BA0AF] text-center mb-5'>{t('温馨提示：请按要求上传照片，避免审核驳回。')}</div>
          <CustomButton onClick={handleNext} disabled={isButtonDisabled} loading={submitting || uploading}>
            {t('提交')}
          </CustomButton>
        </FixedBottom>
      )}
    </div>
  )
}
